/**
 * This is a NON ACTION component, that handles the submission of the responses users have received from their MP,
 * after a they have contacted their MP using a SAM action.
 *
 *
 * As it is a NON ACTION component (i.e. not part of a flow journey), it does not receive that same data and scaffolding
 * as an action component, which can be discombobulating at first read.
 * The component has the following features:
 * It defines a route with the URL prefix "send-a-message/submit-response" using the createFrontendMemberRoute function.
 * It defines a set of state variables using React hooks:
 * who: the name of the MP
 * response: the MP's response text
 * showNextSteps: whether to show a "Thank you" message after submitting the response
 * errorMessage: any error message to be displayed
 * consentConfigs: configuration information for user consent
 * consents: an array of user consents
 *
 * It fetches the consent configuration information using the getTargetResponseConsentConfigs function and sets the default
 *   consents using the getDefaultConsentsFromConfigs function.
 * The submitResponse function handles the response submission by sending the data to the server using the submitTargetResponse
 *   function. If the submission is successful, it sets the showNextSteps state to true, otherwise, it displays an error message.
 * The component renders a loading spinner while waiting for the consent configurations.
 * Once loaded, it displays a form for the user to enter their MP's name and response, consent to the required conditions, and
 *   submit the form.
 * After a successful submission, the component displays a "Thank you" message and a button to view more campaigns.
 */
import { updateConsents } from "@flow/common/consent_logic";
import { Consent, ConsentConfig } from "@flow/common/types";
import { ConsentInputComponentBlock } from "@flow/frontend/common/ConsentInputComponentBlock";
import { createFrontendMemberRoute } from "@flow/frontend/member/FrontendMemberRoute";
import { MainContainer } from "@flow/frontend/member/components/MainContainer";
import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";
import { TextArea } from "@flow/frontend/member/components/TextArea";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { Alert, Button, TextField, Typography, useMediaQuery } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import * as React from "react";
import * as Safe from "safe-portals";
import { getTargetResponseConsentConfigs, submitTargetResponse } from "../../common/routes";

const argsSerializer = Safe.obj({
  actionGuid: Safe.uuid,
  email: Safe.str,
});

type ArgsType = Safe.TypeIn<typeof argsSerializer>;

function SubmitResponseComponent(props: ArgsType) {
  const [who, setWho] = React.useState<string | undefined>(undefined);
  const [support, setSupport] = React.useState<string>("Don't Know");
  const [response, setResponse] = React.useState<string | undefined>(undefined);
  const [showNextSteps, setShowNextSteps] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
  const [consentConfigs, setConsentConfigs] = React.useState<ConsentConfig[] | undefined>(undefined);
  const [consents, setConsents] = React.useState<Consent[]>([]);

  React.useEffect(() => {
    getTargetResponseConsentConfigs.call().then((consentConfigs) => {
      setConsentConfigs(consentConfigs);
    });
  }, []);

  async function submitResponse(): Promise<void> {
    if (consentConfigs && who && response) {
      setErrorMessage(undefined);
      try {
        await submitTargetResponse.call({
          actionGuid: props.actionGuid,
          userEmail: props.email,
          whoResponded: who,
          response,
          decisionMakerSupport: support,
          consents,
        });
        setShowNextSteps(true);
      } catch {
        setShowNextSteps(false);
        setErrorMessage("Something went wrong");
      }
    }
  }

  const handleSupportChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSupport((event.target as HTMLInputElement).value);
  };

  const isSmallScreen = useMediaQuery(memberTheme.breakpoints.down("sm"));

  return consentConfigs === undefined ? (
    <MemberLoadingSpinner />
  ) : (
    <MainContainer sx={{ marginY: "1.5rem" }}>
      {errorMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errorMessage}
          <br />
          Please check what you have entered and try again. If the problem continues please wait a minute and try again.
        </Alert>
      )}
      {showNextSteps ? (
        <>
          <Typography sx={{ typography: { xs: "h5", md: "h4" }, mb: 2 }}>
            Thank you for submitting your response.
          </Typography>
          <Button
            href="https://home.38degrees.org.uk"
            fullWidth={isSmallScreen}
            size="large"
            sx={{
              "&:hover": {
                color: "white", // prevent colour change on hover
              },
            }}
          >
            See more campaigns
          </Button>
        </>
      ) : (
        <>
          <Typography sx={{ typography: { xs: "h5", md: "h4" }, mb: 2 }}>Please share the response below</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Knowing if you get a reply on an issue will help us track where decision makers stand, and will also allow
            us to hold them to account if they say one thing - and do another! Please use the form below to tell us who
            you get a response from, and what they said in their reply.
          </Typography>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitResponse();
            }}
          >
            <TextField
              sx={{ mb: 2 }}
              required
              fullWidth
              value={who}
              onChange={(e) => setWho(e.target.value)}
              label="Who is the response from?"
            />
            <FormLabel id="mp-response-radio-buttons-group-label">Did they support your email?</FormLabel>
            <RadioGroup
              aria-labelledby="mp-response-radio-buttons-group-label"
              value={support}
              onChange={handleSupportChange}
              row
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
              <FormControlLabel value="Don't Know" control={<Radio />} label="Don't Know" />
            </RadioGroup>
            <TextArea sx={{ mb: 2 }} required minRows={4} value={response} onChange={setResponse} label="Response" />
            <ConsentInputComponentBlock
              consentConfigs={consentConfigs}
              updateConsents={(changedConsents) => setConsents((c) => updateConsents(c, changedConsents))}
              alreadyConsentedTo={undefined}
            />
            <Button sx={{ my: 1 }} fullWidth={isSmallScreen} size="large" type="submit">
              Submit
            </Button>
          </form>
        </>
      )}
    </MainContainer>
  );
}
// Error:
// http://localhost:8180/page/#send-a-message/submit-response?actionGuid=%22f15faff0-10cc-4b2e-b58a-418e8f309f40%22&email=%22james.h@38degrees.org.uk%22
// Success:
// http://localhost:8180/page/#send-a-message/submit-response?actionGuid=%22b4b9f12e-6c2c-4947-888c-ed1a37f8c066%22&email=%22james.h@38degrees.org.uk%22
createFrontendMemberRoute({
  urlPrefix: "send-a-message/submit-response",
  args: argsSerializer,
  component: SubmitResponseComponent,
});
